(() => {
  const TARGET_SELECTOR = '.kpo-bundle-color-dynamic';
  const OPTION_GROUP_SELECTOR = '[data-kpo-option-name]';
  const LABEL_SELECTOR = '.ymq-option-title .ymq_lable';
  const PROPERTY_FIELD_SELECTOR = 'input[name], select[name], textarea[name]';
  const COLOR_TOKEN_PATTERN = /\{Color\s*[12]\}/i;
  const PROPERTY_NAME_PATTERN = /^(properties|empty-properties)\[(.*)\]$/;
  const LABEL_TEMPLATE_KEY = 'kpoBundleColorTemplate';
  const PROPERTY_TEMPLATE_KEY = 'kpoBundleColorPropertyTemplate';
  const ARIA_TEMPLATE_KEY = 'kpoBundleColorAriaTemplate';
  const richTextTemplates = new WeakMap();

  let mutationObserver = null;
  let scheduledRefresh = false;

  function clean(value) {
    return String(value || '').replace(/[\r\n]+/g, ' ').trim();
  }

  function containsColorToken(value) {
    return COLOR_TOKEN_PATTERN.test(String(value || ''));
  }

  function resolveTemplate(template, colors) {
    return String(template || '')
      .replace(/\{Color\s*1\}/gi, colors[0])
      .replace(/\{Color\s*2\}/gi, colors[1]);
  }

  function getSelectedOptionValue(optionGroup) {
    const select = optionGroup.querySelector('select');
    if (select) return clean(select.value);

    const checkedInput = optionGroup.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (checkedInput) return clean(checkedInput.value);

    const selectedValue = optionGroup.querySelector('[data-selected-value]');
    return selectedValue ? clean(selectedValue.textContent) : '';
  }

  function getSelectedColors(root) {
    const colorGroup = Array.from(root.querySelectorAll(OPTION_GROUP_SELECTOR)).find((optionGroup) => {
      const optionName = clean(optionGroup.dataset.kpoOptionName).toLowerCase();
      return optionName.includes('color') || optionName.includes('colour');
    });

    if (!colorGroup) return null;

    const colors = getSelectedOptionValue(colorGroup)
      .split('/')
      .map((color) => clean(color));

    if (colors.length !== 2 || colors.some((color) => !color)) return null;
    return colors;
  }

  function getProductRoot(target) {
    return target.closest('product-info') || document;
  }

  function prepareLabel(label, colors, resolvedTemplates) {
    let template = label.dataset[LABEL_TEMPLATE_KEY];
    const currentLabel = clean(label.textContent);

    if (!template && containsColorToken(currentLabel)) {
      template = currentLabel;
      label.dataset[LABEL_TEMPLATE_KEY] = template;
    }

    if (!template) return;

    const resolvedLabel = resolveTemplate(template, colors);
    resolvedTemplates.set(clean(resolvedLabel), template);
    if (currentLabel !== resolvedLabel) label.textContent = resolvedLabel;
  }

  function preparePropertyField(field, colors, resolvedTemplates) {
    const currentName = field.getAttribute('name') || '';
    const propertyMatch = currentName.match(PROPERTY_NAME_PATTERN);
    if (!propertyMatch) return;

    let template = field.dataset[PROPERTY_TEMPLATE_KEY];
    const currentPropertyLabel = clean(propertyMatch[2]);

    if (!template && containsColorToken(currentPropertyLabel)) {
      template = currentPropertyLabel;
      field.dataset[PROPERTY_TEMPLATE_KEY] = template;
    }

    if (!template && resolvedTemplates.has(currentPropertyLabel)) {
      template = resolvedTemplates.get(currentPropertyLabel);
      field.dataset[PROPERTY_TEMPLATE_KEY] = template;
    }

    if (!template) return;

    const resolvedName = `${propertyMatch[1]}[${resolveTemplate(template, colors)}]`;
    if (currentName !== resolvedName) field.setAttribute('name', resolvedName);
  }

  function prepareAriaLabel(element, colors, resolvedTemplates) {
    const currentLabel = clean(element.getAttribute('aria-label'));
    let template = element.dataset[ARIA_TEMPLATE_KEY];

    if (!template && containsColorToken(currentLabel)) {
      template = currentLabel;
      element.dataset[ARIA_TEMPLATE_KEY] = template;
    }

    if (!template && resolvedTemplates.has(currentLabel)) {
      template = resolvedTemplates.get(currentLabel);
      element.dataset[ARIA_TEMPLATE_KEY] = template;
    }

    if (!template) return;

    const resolvedLabel = resolveTemplate(template, colors);
    if (currentLabel !== resolvedLabel) element.setAttribute('aria-label', resolvedLabel);
  }

  function prepareRichText(target, colors) {
    const textWalker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    let textNode = textWalker.nextNode();

    while (textNode) {
      const parent = textNode.parentElement;
      const excludedParent = parent
        ? parent.closest(`${LABEL_SELECTOR}, script, style, textarea, select, option`)
        : null;

      if (!excludedParent) {
        let template = richTextTemplates.get(textNode);
        const currentText = textNode.nodeValue || '';

        if (!template && containsColorToken(currentText)) {
          template = currentText;
          richTextTemplates.set(textNode, template);
        }

        if (template) {
          const resolvedText = resolveTemplate(template, colors);
          if (currentText !== resolvedText) textNode.nodeValue = resolvedText;
        }
      }

      textNode = textWalker.nextNode();
    }
  }

  function refreshProductRoot(root, targets) {
    const colors = getSelectedColors(root);
    if (!colors) return;

    const resolvedTemplates = new Map();
    targets.forEach((target) => {
      target.querySelectorAll(LABEL_SELECTOR).forEach((label) => {
        prepareLabel(label, colors, resolvedTemplates);
      });
      prepareRichText(target, colors);
    });

    root.querySelectorAll(PROPERTY_FIELD_SELECTOR).forEach((field) => {
      preparePropertyField(field, colors, resolvedTemplates);
    });

    root.querySelectorAll('[aria-label]').forEach((element) => {
      prepareAriaLabel(element, colors, resolvedTemplates);
    });
  }

  function refresh() {
    const targetsByRoot = new Map();

    document.querySelectorAll(TARGET_SELECTOR).forEach((target) => {
      const root = getProductRoot(target);
      if (!targetsByRoot.has(root)) targetsByRoot.set(root, []);
      targetsByRoot.get(root).push(target);
    });

    targetsByRoot.forEach((targets, root) => {
      refreshProductRoot(root, targets);
    });
  }

  function scheduleRefresh() {
    if (scheduledRefresh) return;
    scheduledRefresh = true;
    window.requestAnimationFrame(() => {
      scheduledRefresh = false;
      refresh();
    });
  }

  function attachObserver() {
    if (mutationObserver || !document.body) return;

    mutationObserver = new MutationObserver(() => {
      scheduleRefresh();
    });

    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['name'],
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function init() {
    refresh();
    attachObserver();
  }

  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-selects')) scheduleRefresh();
  });

  document.addEventListener(
    'submit',
    (event) => {
      const root = event.target.closest('product-info') || document;
      const targets = Array.from(root.querySelectorAll(TARGET_SELECTOR));
      if (targets.length > 0) refreshProductRoot(root, targets);
    },
    true
  );

  document.addEventListener('shopify:section:load', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
